import React, { useState, useEffect } from 'react';
import axiosInstance from '../../../api/axiosConfig';
import './OrganizationModals.css';

const BulkUploadSalesReportModal = ({ show, onClose, onSuccess, companies }) => {
    const [formData, setFormData] = useState({
        companyId: '',
        month: new Date().getMonth() + 1,
        year: new Date().getFullYear(),
        day: new Date().getDate(),
        file: null
    });
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [uploadResult, setUploadResult] = useState(null);
    const [dragActive, setDragActive] = useState(false);

    useEffect(() => {
        if (show) {
            // Reset form when modal opens
            setFormData({
                companyId: '',
                month: new Date().getMonth() + 1,
                year: new Date().getFullYear(),
                day: new Date().getDate(),
                file: null
            });
            setError(null);
            setUploadResult(null);
        }
    }, [show]);

    const handleFileChange = (file) => {
        if (file) {
            // Validate file type
            const validTypes = [
                'application/vnd.ms-excel',
                'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
                'text/csv'
            ];
            
            if (!validTypes.includes(file.type) && !file.name.match(/\.(xlsx?|csv)$/i)) {
                setError('Please select a valid Excel file (.xlsx, .xls, .csv)');
                return;
            }

            // Check file size (10MB limit)
            if (file.size > 10 * 1024 * 1024) {
                setError('File size must be less than 10MB');
                return;
            }

            setFormData(prev => ({ ...prev, file }));
            setError(null);
        }
    };

    const handleDrag = (e) => {
        e.preventDefault();
        e.stopPropagation();
        if (e.type === 'dragenter' || e.type === 'dragover') {
            setDragActive(true);
        } else if (e.type === 'dragleave') {
            setDragActive(false);
        }
    };

    const handleDrop = (e) => {
        e.preventDefault();
        e.stopPropagation();
        setDragActive(false);
        
        if (e.dataTransfer.files && e.dataTransfer.files[0]) {
            handleFileChange(e.dataTransfer.files[0]);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);
        setUploadResult(null);

        try {
            if (!formData.file) {
                setError('Please select a file to upload');
                return;
            }

            const uploadFormData = new FormData();
            uploadFormData.append('file', formData.file);
            uploadFormData.append('companyId', formData.companyId);
            uploadFormData.append('month', formData.month.toString());
            uploadFormData.append('year', formData.year.toString());
            uploadFormData.append('day', formData.day.toString());

            const response = await axiosInstance.post(
                '/api/sales-target/reports/bulk-upload',
                uploadFormData
            );

            if (response.data.success) {
                setUploadResult(response.data.data);
                if (response.data.data.success > 0) {
                    // Call onSuccess to refresh the parent components
                    onSuccess();
                }
            }
        } catch (err) {
            setError(err.response?.data?.message || 'Failed to upload sales report');
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        if (!loading) {
            setFormData({
                companyId: '',
                month: new Date().getMonth() + 1,
                year: new Date().getFullYear(),
                day: new Date().getDate(),
                file: null
            });
            setError(null);
            setUploadResult(null);
            onClose();
        }
    };

    const getMonthName = (month) => {
        return new Date(2024, month - 1).toLocaleString('default', { month: 'long' });
    };

    if (!show) return null;

    return (
        <div className="modal-overlay" onClick={handleClose}>
            <div className="modal-content bulk-upload-modal" onClick={(e) => e.stopPropagation()}>
                <h2>Bulk Upload Sales Reports</h2>
                <p className="modal-subtitle">Upload Excel file with sales data for multiple salesmen</p>

                {error && <div className="error-message">{error}</div>}

                {uploadResult ? (
                    // Results Display
                    <div className="upload-results">
                        <div className="results-header">
                            <h3>Upload Results</h3>
                            <div className="results-summary">
                                <span className="success-count">✅ {uploadResult.success} Successful</span>
                                <span className="failed-count">❌ {uploadResult.failed} Failed</span>
                            </div>
                        </div>

                        {uploadResult.columnMapping && (
                            <div className="column-mapping">
                                <h4>Detected Columns:</h4>
                                <div className="mapping-grid">
                                    {Object.entries(uploadResult.columnMapping).map(([key, index]) => (
                                        <div key={key} className="mapping-item">
                                            <span className="column-name">{key}</span>
                                            <span className="column-index">Column {String.fromCharCode(65 + index)}</span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        )}

                        {uploadResult.errors && uploadResult.errors.length > 0 && (
                            <div className="error-details">
                                <h4>Error Details:</h4>
                                <div className="error-list">
                                    {uploadResult.errors.slice(0, 10).map((error, index) => (
                                        <div key={index} className="error-item">
                                            {error}
                                        </div>
                                    ))}
                                    {uploadResult.errors.length > 10 && (
                                        <div className="error-item more-errors">
                                            ... and {uploadResult.errors.length - 10} more errors
                                        </div>
                                    )}
                                </div>
                            </div>
                        )}

                        <div className="modal-actions">
                            <button 
                                type="button" 
                                onClick={handleClose} 
                                className="btn-primary"
                            >
                                Close
                            </button>
                        </div>
                    </div>
                ) : (
                    // Upload Form
                    <form onSubmit={handleSubmit}>
                        <div className="form-row">
                            <div className="form-group">
                                <label>Year *</label>
                                <select
                                    id="bulkUploadYear"
                                    value={formData.year}
                                    onChange={(e) => setFormData({ ...formData, year: parseInt(e.target.value) })}
                                    required
                                    disabled={loading}
                                >
                                    {[2024, 2025, 2026].map(year => (
                                        <option key={year} value={year}>{year}</option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group">
                                <label>Month *</label>
                                <select
                                    id="bulkUploadMonth"
                                    value={formData.month}
                                    onChange={(e) => setFormData({ ...formData, month: parseInt(e.target.value) })}
                                    required
                                    disabled={loading}
                                >
                                    {Array.from({ length: 12 }, (_, i) => i + 1).map(month => (
                                        <option key={month} value={month}>
                                            {getMonthName(month)}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div className="form-group">
                                <label>Day *</label>
                                <select
                                    id="bulkUploadDay"
                                    value={formData.day}
                                    onChange={(e) => setFormData({ ...formData, day: parseInt(e.target.value) })}
                                    required
                                    disabled={loading}
                                >
                                    {Array.from({ length: 31 }, (_, i) => i + 1).map(day => (
                                        <option key={day} value={day}>{day}</option>
                                    ))}
                                </select>
                            </div>
                        </div>

                        <div className="form-group">
                            <label>Company *</label>
                            <select
                                id="companySelect"
                                value={formData.companyId}
                                onChange={(e) => setFormData({ ...formData, companyId: e.target.value })}
                                required
                                disabled={loading}
                            >
                                <option value="">-- Select Company --</option>
                                {companies.map(company => (
                                    <option key={company._id} value={company._id}>
                                        {company.name}
                                    </option>
                                ))}
                            </select>
                            <small>Salesmen will be identified by Employee ID and matched to their respective branches</small>
                        </div>

                        <div className="form-group">
                            <label>Sales Report File *</label>
                            <div 
                                className={`file-upload-area ${dragActive ? 'drag-active' : ''} ${formData.file ? 'has-file' : ''}`}
                                onDragEnter={handleDrag}
                                onDragLeave={handleDrag}
                                onDragOver={handleDrag}
                                onDrop={handleDrop}
                            >
                                <input
                                    type="file"
                                    id="file-input"
                                    accept=".xlsx,.xls,.csv"
                                    onChange={(e) => handleFileChange(e.target.files[0])}
                                    style={{ display: 'none' }}
                                    disabled={loading}
                                />
                                
                                {formData.file ? (
                                    <div className="file-selected">
                                        <div className="file-icon">📄</div>
                                        <div className="file-info">
                                            <div className="file-name">{formData.file.name}</div>
                                            <div className="file-size">
                                                {(formData.file.size / 1024 / 1024).toFixed(2)} MB
                                            </div>
                                        </div>
                                        <button
                                            type="button"
                                            className="remove-file-btn"
                                            onClick={() => setFormData({ ...formData, file: null })}
                                            disabled={loading}
                                        >
                                            ✕
                                        </button>
                                    </div>
                                ) : (
                                    <div className="file-upload-prompt">
                                        <div className="upload-icon">📁</div>
                                        <div className="upload-text">
                                            <strong>Click to select</strong> or drag and drop
                                        </div>
                                        <div className="upload-hint">
                                            Excel files (.xlsx, .xls, .csv) up to 10MB
                                        </div>
                                        <label htmlFor="file-input" className="file-select-btn">
                                            Choose File
                                        </label>
                                    </div>
                                )}
                            </div>
                        </div>

                        <div className="info-box">
                            <h4>📋 Expected File Format:</h4>
                            <ul>
                                <li><strong>Sales Engineer column:</strong> Format "B1010-Hamas Hashim" (EmployeeID-Name)</li>
                                <li><strong>Sales columns:</strong> Cash Invoice, Credit Invoice, Pos Sales, Total</li>
                                <li><strong>Headers:</strong> Will be detected automatically</li>
                                <li><strong>Duplicates:</strong> Existing reports will be updated</li>
                            </ul>
                        </div>

                        <div className="modal-actions">
                            <button 
                                type="button" 
                                onClick={handleClose} 
                                className="btn-secondary" 
                                disabled={loading}
                            >
                                Cancel
                            </button>
                            <button 
                                type="submit" 
                                className="btn-primary" 
                                disabled={loading || !formData.file || !formData.companyId}
                            >
                                {loading ? (
                                    <>
                                        <span className="loading-spinner"></span>
                                        Processing...
                                    </>
                                ) : (
                                    'Upload Reports'
                                )}
                            </button>
                        </div>
                    </form>
                )}
            </div>
        </div>
    );
};

export default BulkUploadSalesReportModal;